import { VIEWING_SLOT_LABELS, type Viewing, type ViewingDog, type ViewingSlot, type ViewingStatus } from '@bbbk/schema';
import { dateOnly } from '../../lib/dates';
import type { BookingFlag } from '../bookings/flags';
import { breedLabel } from '../bookings/domain';

// THE VIEWINGS DERIVATION — the one place that answers what a viewing means on
// screen. The desk row, the desk count and (with viewings-in-needs-attention)
// the rail's third counter all read from here, so they cannot come to describe
// different sets. Same rule that already binds deriveNeedsAttention to
// attentionBookings, and for the same reason: the surfaces sit inches apart.

/**
 * THE ONE ORDER every list of viewings is read in: by day, then morning before
 * afternoon, then by id.
 *
 * SORTED SO ROWS HOLD THEIR PLACE. The desk sorts its bookings for the same
 * reason — a row that moves when its status flips is a row somebody presses by
 * mistake — and a viewing's status flips from the desk itself. One comparator,
 * because the desk sorted by slot and the chase list by day-then-id, and an
 * afternoon viewing sat above a morning one in the panel on the same day
 * (review, PR #173).
 */
export function compareViewings(a: Viewing, b: Viewing): number {
  const da = dateOnly(a.viewingOn);
  const db = dateOnly(b.viewingOn);
  if (da !== db) return da < db ? -1 : 1;
  if (a.slot !== b.slot) return a.slot === 'morning' ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The viewings on one day, in the one order.
 */
export function viewingsOnDay(viewings: Viewing[], iso: string): Viewing[] {
  const day = dateOnly(iso);
  return viewings
    // dateOnly on BOTH sides. The column is selected ::text so the wire should
    // carry a bare day, but this app has been handed a timestamp where a date
    // was promised before, and a silent mismatch here would empty the desk.
    .filter((v) => dateOnly(v.viewingOn) === day)
    .sort(compareViewings);
}

/**
 * The desk's optimistic status, laid over ANY set of viewings — the desk's own
 * day, or the seven-day look-ahead behind the attention bar. The overlay used
 * to be applied to the desk rows alone, so pressing Visited flipped the pill at
 * once while the bar's count and the panel's row waited two round trips for the
 * server to say the same thing (review, PR #173).
 *
 * Returns the SAME array when nothing is pending, so a memoised consumer keyed
 * on the result does not re-run for an empty overlay.
 */
export function overlayViewingStatus(viewings: Viewing[], pending: Record<string, ViewingStatus>): Viewing[] {
  if (Object.keys(pending).length === 0) return viewings;
  return viewings.map((v) => (pending[v.id] ? { ...v, status: pending[v.id]! } : v));
}

/**
 * The viewings the attention bar counts: still EXPECTED, and falling inside the
 * look-ahead window.
 *
 * NOT "every viewing in the window". A Visited or No-show viewing dated Friday
 * is settled — there is no work left in it — and leaving it in the count would
 * stop the strip ever reaching its all-clear, on a week where everything had in
 * fact been dealt with.
 *
 * A viewing that already has a booking is still counted while it is Expected:
 * the visit has yet to happen, and booking somebody early does not mean they
 * have been. Status answers "did the visit happen"; `booking_id` does not.
 */
export function expectedInWindow(viewings: Viewing[], from: string, to: string): Viewing[] {
  const lo = dateOnly(from);
  const hi = dateOnly(to);
  return viewings
    .filter((v) => v.status === 'expected')
    .filter((v) => {
      const day = dateOnly(v.viewingOn);
      return day >= lo && day <= hi;
    })
    .sort(compareViewings);
}

/**
 * Has the visit been settled either way? Decided by STATUS ALONE.
 *
 * A settled row greys out, exactly as a collected dog does. Whether it produced
 * a booking is a different question and must not reach this one.
 */
export function isSettled(v: Viewing): boolean {
  return v.status === 'visited' || v.status === 'no_show';
}

/**
 * Does this viewing still need booking? KEYED ON THE BOOKING, NEVER ON STATUS.
 *
 * A viewing can be Visited, with a household saved, and still have no booking
 * (book-them-in AC5b) — and that is precisely the state that most needs the
 * action. A rule keyed on "settled" hid `Book` on every visited row and defeated
 * it (codex, PR #168), which is why this is a named function rather than an
 * inline `!isSettled(v)` at the call site.
 */
export function canBook(v: Viewing): boolean {
  return v.bookingId === null;
}

/**
 * What pressing the desk's status pill produces.
 *
 * Expected advances to Visited — the identical gesture that turns Confirmed into
 * Arrived on every other desk row, so nothing new is learned. Anything settled
 * goes back to Expected, so a mis-press costs one press to undo and the pill is
 * never a dead end. No-show is only SET in the modal; it must still be
 * correctable from the row.
 */
export function pillTarget(status: ViewingStatus): ViewingStatus {
  return status === 'expected' ? 'visited' : 'expected';
}

/** The suffix beside the Drop-offs count — null when there is nothing to say. */
export function viewingCountLabel(n: number): string | null {
  return n === 0 ? null : `${n} viewing${n === 1 ? '' : 's'}`;
}

/**
 * A viewing's dog names for the desk cell — the em dash holding the place of a
 * name nobody gave, so a row that mentioned two dogs shows two.
 */
const NONE = '\u2014';

export function viewingDogNames(dogs: ViewingDog[]): string {
  if (dogs.length === 0) return '';
  const parts = dogs.map((d) => (d.name ?? '').trim() || NONE);
  return parts.every((p) => p === NONE) ? '' : parts.join(' \u00b7 ');
}

/**
 * And its breed line — ONE breed, or 'Mixed'. Identical rule to a booking's,
 * and the same function underneath: two kinds of row that read alike must not
 * describe their dogs by two different rules (Sir, 2026-08-27).
 */
export function viewingDogBreeds(dogs: ViewingDog[]): string {
  return breedLabel(dogs);
}

export function viewingSlotLabel(slot: ViewingSlot): string {
  return VIEWING_SLOT_LABELS[slot];
}

/**
 * The Viewing tag, in the shared flag shape.
 *
 * Built here rather than in deriveBookingFlags because a viewing is not a
 * booking and has none of the fields that derivation reads — but it renders
 * through the same FlagStrip, so the row cannot acquire a second badge idiom.
 */
export function viewingFlags(): BookingFlag[] {
  return [
    {
      kind: 'viewing',
      scope: 'household',
      icon: 'eye',
      tint: 'viewing',
      label: 'Viewing',
      detail: 'Somebody coming to look round — not a stay',
    },
  ];
}

/**
 * May a HELD read answer for `day`? The desk's read is a ±2-day window, so a
 * one-day step lands inside the window it already holds; asking "is this the
 * window I just requested?" instead marked the viewings "not read" for the
 * frames until the new read landed — a flicker on every step (Sir,
 * 2026-09-02). A held window that covers the day is a true answer for it.
 */
export function windowCovers(w: { from: string; to: string } | null, day: string): boolean {
  if (!w) return false;
  const d = dateOnly(day);
  return d >= dateOnly(w.from) && d <= dateOnly(w.to);
}
